import { faUser } from "@fortawesome/pro-regular-svg-icons"
import { faPauseCircle, faPenCircle, faPlayCircle, faPlusCircle, faUserCheck } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Button, MenuItem, Select, Typography } from "@mui/material"
import { DataGrid, GridColDef } from "@mui/x-data-grid"
import { translate } from "app/language/service"
import { IRootState } from "app/model"
import React from "react"
import { connect } from "react-redux"
import { FlexBox } from "ui/components/FlexBox"
import { LinearLoader } from "ui/components/LinearLoader"
import { ListIdCell } from "ui/components/ListIdCell"
import { ListActionButton } from "ui/components/main-layout/ListActionButton"
import { getDataGridTexts } from "ui/tools/list"
import { IUser, IUserInfo } from "user/model"
import { getMyAgencyMembers } from "user/tools/agency"
import { getUserId, setUserActivation } from "user/tools/common"
import {
  canSavePasswordSelf,
  canSaveUserAgency,
  canSaveUserRoleAgency,
  canSaveUserSelf,
  isAgencyAdmin as isAgencyAdminFn
} from "user/tools/permissions"
import { UserFormPopup } from "./UserFormPopup"
import { UserRoles } from "./UserRoles"

interface IAgencyMembersProps {
  isAgencyAdmin: boolean
  selfUserInfo: IUserInfo
}

const AgencyMembersFn = ({ isAgencyAdmin, selfUserInfo }: IAgencyMembersProps) => {
  const [users, setUsers] = React.useState<null | IUser[]>(null)
  const [selectedUserId, setSelectedUserId] = React.useState<null | number>(null)
  const [hasLoadingError, setHasLoadingError] = React.useState(false)
  const [isChangingActivation, setIsChangingActivation] = React.useState(false)
  const [isPopupOpened, setIsPopupOpened] = React.useState(false)
  const [activeFilter, setActiveFilter] = React.useState<"active" | "inactive" | "both">("active")
  const isSelf = React.useMemo(() => selectedUserId === getUserId(selfUserInfo), [selfUserInfo, selectedUserId])
  const refreshList = () =>
    getMyAgencyMembers()
      .then(newUsers => setUsers(newUsers))
      .catch(error => {
        console.error(error)
        setHasLoadingError(true)
      })
  React.useEffect(() => {
    refreshList()
  }, [])
  const columns: Array<GridColDef<IUser>> = [
    {
      field: "email",
      headerName: translate("label.email"),
      flex: 3,
      renderCell: params => (
        <ListIdCell
          id={params.row.id}
          label={params.row.email}
          onClick={() => {
            setSelectedUserId(params.row.id)
            setIsPopupOpened(true)
          }}
        />
      )
    },
    {
      field: "active",
      headerName: translate("label.active"),
      flex: 1,
      renderCell: params => (
        <FlexBox
          sx={{
            "& .user": { cursor: "pointer", color: theme => theme.palette.primary.main },
            "& .user.inactive": { cursor: "pointer", color: theme => theme.palette.text.disabled }
          }}>
          <FontAwesomeIcon
            icon={params.row.active ? faUserCheck : faUser}
            className={params.row.active ? "user" : "user inactive"}
            title={translate(params.row.active ? "label.active" : "label.inactive")}
          />
        </FlexBox>
      )
    },
    {
      field: "firstname",
      headerName: translate("label.firstname"),
      flex: 1
    },
    {
      field: "lastname",
      headerName: translate("label.lastname"),
      flex: 1
    },
    {
      field: "roles",
      headerName: translate("label.roles"),
      flex: 2,
      sortable: false,
      renderCell: params => <UserRoles userId={params.row.id} />
    }
  ]
  if (isAgencyAdmin) {
    columns.push({
      field: "actions",
      headerName: translate("label.actions"),
      flex: 1,
      renderCell: params => (
        <FlexBox gap>
          <ListActionButton
            id={`user-action-update-${params.row.email}`}
            icon={faPenCircle}
            tooltip={translate("user.update.title")}
            onClick={() => {
              setSelectedUserId(params.row.id)
              setIsPopupOpened(true)
            }}
          />
          <ListActionButton
            id={`user-action-activation-${params.row.email}`}
            disabled={isChangingActivation || params.id === selfUserInfo.user.id}
            className={params.row.active ? "active" : "inactive"}
            icon={params.row.active ? faPauseCircle : faPlayCircle}
            tooltip={translate(params.row.active ? "user.deactivate.title" : "user.activate.title")}
            onClick={() => {
              if (isChangingActivation) {
                return
              }
              setIsChangingActivation(true)
              setUserActivation(params.row.id, params.row.email, !params.row.active)
                .then(updatedUser => {
                  if (updatedUser) {
                    setUsers((users as IUser[]).map(u => (u.id === params.row.id ? updatedUser : u)))
                  }
                })
                .finally(() => setIsChangingActivation(false))
            }}
          />
        </FlexBox>
      )
    })
  }
  return (
    <FlexBox id="agency-member-list" vertical fullWidth ai="start">
      {hasLoadingError ? (
        <Typography color="error" fontSize=".9rem">
          {translate("user.members.loading.error")}
        </Typography>
      ) : users === null ? (
        <LinearLoader />
      ) : (
        <>
          {isAgencyAdmin && (
            <FlexBox fullWidth jc="space-between">
              <FlexBox>
                <Select
                  id="agency-members-active-filter"
                  size="small"
                  value={activeFilter}
                  onChange={event => setActiveFilter(event.target.value as "active" | "inactive" | "both")}>
                  <MenuItem id="agency-members-active-filter-option-active" value="active">
                    {translate("label.active")}
                  </MenuItem>
                  <MenuItem id="agency-members-active-filter-option-inactive" value="inactive">
                    {translate("label.inactive")}
                  </MenuItem>
                  <MenuItem id="agency-members-active-filter-option-both" value="both">
                    {translate("label.all")}
                  </MenuItem>
                </Select>
              </FlexBox>
              <Button
                id="agency-add-member"
                variant="outlined"
                startIcon={<FontAwesomeIcon icon={faPlusCircle} size="1x" />}
                onClick={() => setIsPopupOpened(true)}>
                {translate("button.add")}
              </Button>
            </FlexBox>
          )}
          <FlexBox
            fullWidth
            vertical
            sx={{
              "position": "relative",
              "height": "26rem",
              "minHeight": "26rem",
              "maxHeight": "26rem",
              "transition": "all 400ms ease",
              "& > *": {
                width: "100%"
              },
              "& .MuiDataGrid-selectedRowCount": {
                visibility: "hidden"
              },
              "& .MuiDataGrid-root": {
                border: "none"
              },
              "& .MuiDataGrid-cell": {
                fontSize: ".8rem",
                fontWeight: 300
              },
              "& .MuiDataGrid-row": {
                cursor: "pointer"
              },
              "& .MuiDataGrid-row.Mui-selected": {
                backgroundColor: "inherit"
              },
              "& .MuiDataGrid-columnHeader, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-cell:focus, & .MuiDataGrid-cell":
                {
                  outline: "none !important"
                }
            }}>
            {isChangingActivation && <FlexBox id="is-changing-activation-hidden-for-test" sx={{ display: "none" }} />}
            <DataGrid
              rows={
                activeFilter === "both"
                  ? users
                  : activeFilter === "active"
                    ? users.filter(u => u.active)
                    : users.filter(u => !u.active)
              }
              columns={columns}
              className="agency-members"
              rowHeight={40}
              autoPageSize
              disableSelectionOnClick
              localeText={getDataGridTexts()}
            />
          </FlexBox>
          {isPopupOpened && (
            <UserFormPopup
              allUsers={users}
              onClose={() => {
                if (typeof selectedUserId === "number") {
                  setSelectedUserId(null)
                }
                setIsPopupOpened(false)
              }}
              onSave={user => {
                if (typeof selectedUserId === "number") {
                  setUsers(users.map(u => (u.id === user.id ? user : u)))
                } else {
                  setUsers([...users, user])
                }
                setSelectedUserId(null)
                setIsPopupOpened(false)
              }}
              onRoleChange={user => {
                setUsers(null)
                refreshList()
              }}
              userId={selectedUserId}
              disableUserEdit={!((isSelf && canSaveUserSelf(selfUserInfo)) || canSaveUserAgency(selfUserInfo))}
              disableUserRole={isSelf || !canSaveUserRoleAgency(selfUserInfo)}
              disableUserPassword={!(isSelf && canSavePasswordSelf())}
            />
          )}
        </>
      )}
    </FlexBox>
  )
}

export const AgencyMembers = connect(
  (state: IRootState): IAgencyMembersProps => ({
    isAgencyAdmin: isAgencyAdminFn(state.user),
    selfUserInfo: state.user
  })
)(AgencyMembersFn)
